import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

const PolitiqueConfidentialite = () => {
  return (
    <>
      <Helmet>
        <title>Politique de Confidentialité - Roca Rio | Protection des Données</title>
        <meta name="description" content="Politique de confidentialité de Roca Rio. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <Header />
      
      <main className="bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
          <header className="mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">Politique de Confidentialité</h1>
            <p className="text-lg text-muted-foreground">Comment nous protégeons et utilisons vos données personnelles</p>
          </header>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Collecte des données</h2>
              <p className="text-muted-foreground mb-4">
                Nous collectons uniquement les informations nécessaires pour :
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Répondre à vos demandes via le formulaire de contact ou par WhatsApp</li>
                <li>Gérer vos réservations d'excursions</li>
                <li>Vous transmettre des informations utiles (ex : bons plans, actualités)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Types de données collectées</h2>
              <div className="bg-muted/50 p-6 rounded-lg">
                <ul className="space-y-2">
                  <li><strong>Nom, prénom</strong></li>
                  <li><strong>Adresse e-mail</strong></li>
                  <li><strong>Numéro de téléphone</strong></li>
                  <li><strong>Informations liées à la réservation</strong> (date, excursion choisie)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Utilisation des données</h2>
              <p className="text-muted-foreground">
                Vos données ne sont utilisées qu'à des fins de gestion des excursions et de communication avec vous. 
                Elles ne sont en aucun cas revendues à des tiers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Durée de conservation</h2>
              <p className="text-muted-foreground">
                Les données sont conservées pendant la durée nécessaire à la relation commerciale, 
                et jusqu'à 3 ans après le dernier contact.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Sécurité des données</h2>
              <p className="text-muted-foreground">
                Nous mettons en place des mesures techniques pour protéger vos données contre tout accès non autorisé.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Vos droits</h2>
              <div className="bg-primary/5 border-l-4 border-primary p-6">
                <p className="text-muted-foreground mb-4">
                  Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données.
                </p>
                <p className="text-muted-foreground">
                  <strong>Pour exercer vos droits, contactez-nous à :</strong> contact.rocario@gmail.com
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Cookies et Outils d'Analyse</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Types de cookies utilisés</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site (navigation, sécurité)</li>
                    <li><strong>Cookies analytiques :</strong> Google Analytics pour comprendre l'utilisation du site</li>
                    <li><strong>Cookies marketing :</strong> Personnalisation des contenus et mesure publicitaire</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Google Analytics</h3>
                  <p className="text-muted-foreground mb-2">
                    Nous utilisons Google Analytics pour analyser l'utilisation de notre site web. Cet outil collecte des informations anonymisées sur :
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 mb-3">
                    <li>Les pages visitées et le temps passé sur le site</li>
                    <li>La provenance géographique des visiteurs (ville/région)</li>
                    <li>Les appareils et navigateurs utilisés</li>
                    <li>Les sources de trafic (moteurs de recherche, réseaux sociaux, etc.)</li>
                  </ul>
                  <p className="text-muted-foreground text-sm">
                    Les adresses IP sont anonymisées et aucune donnée personnelle identifiable n'est collectée. 
                    Vous pouvez refuser ces cookies via notre bandeau de consentement.
                  </p>
                </div>
                
                <div className="bg-primary/5 border-l-4 border-primary p-4">
                  <p className="text-muted-foreground">
                    <strong>Gestion des cookies :</strong> Vous pouvez modifier vos préférences à tout moment via le lien "Paramètres des cookies" en bas de page, 
                    ou directement dans les paramètres de votre navigateur.
                  </p>
                </div>
              </div>
            </section>

            <section className="border-t pt-8">
              <h2 className="text-2xl font-semibold text-foreground mb-6">Conditions Particulières</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Annulation & Sécurité</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Les excursions sont confirmées par WhatsApp ou e-mail</li>
                    <li>En cas de météo défavorable ou de problème de sécurité, l'excursion pourra être annulée ou reportée par l'organisateur</li>
                    <li>Les participants sont responsables de vérifier qu'ils disposent d'une assurance voyage personnelle couvrant les activités prévues</li>
                    <li>Certaines excursions nécessitent une bonne condition physique et l'habitude de la randonnée : il appartient à chaque participant de s'assurer qu'il est apte à y prendre part</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Photographies & Vidéos</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Lors des excursions, des photos ou vidéos peuvent être prises à des fins de communication et de promotion</li>
                    <li>Si vous ne souhaitez pas apparaître, merci de nous le signaler avant le début de l'activité</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Paiements</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Les prix affichés correspondent à un règlement en espèces</li>
                    <li>En cas de paiement par carte bancaire, des frais supplémentaires de 5% sont appliqués pour couvrir les frais bancaires</li>
                  </ul>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default PolitiqueConfidentialite;